import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Litters.css';

const LitterDisplay = ({
  litter, dogLookup, kennelLookup, playerId, playerUsername, setLitters, setMessage, deleteLitter
}) => (
  <div key={litter._id} className="litter-container">
    <div className="litter-parents">
      <div className="parent-image">
        <Link to={`/dog/${litter.sireId}`}>
          <img
            src={dogLookup[litter.sireId]?.artUrl || 'https://via.placeholder.com/100?text=Sire'}
            alt="Sire"
            onError={e => (e.target.src = 'https://via.placeholder.com/100?text=Sire')}
          />
        </Link>
      </div>
      <span className="parent-divider">X</span>
      <div className="parent-image">
        <Link to={`/dog/${litter.damId}`}>
          <img
            src={dogLookup[litter.damId]?.artUrl || 'https://via.placeholder.com/100?text=Dam'}
            alt="Dam"
            onError={e => (e.target.src = 'https://via.placeholder.com/100?text=Dam')}
          />
        </Link>
      </div>
    </div>
    <h3>
      Litter: {dogLookup[litter.sireId]?.registeredName || litter.sireId || 'Unknown'} x{' '}
      {dogLookup[litter.damId]?.registeredName || litter.damId || 'Unknown'} - {litter.status}
    </h3>
    <p><strong>Bred By:</strong> {kennelLookup[litter.bredBy] || 'Unknown'}</p>
    {litter.coBreederKennelId && (
      <p><strong>Co-Breeder:</strong> {kennelLookup[litter.coBreederKennelId] || 'Unknown'}</p>
    )}
    <p><strong>Puppies:</strong> {litter.puppies.length} {litter.status === 'Pending' ? '(Pending Roll)' : ''}</p>
    {litter.artUrl ? (
      <img src={litter.artUrl} alt="Litter Art" className="litter-art" />
    ) : (
      <p>No art uploaded.</p>
    )}
    {litter.status === 'Pending' && (
      <div className="co-breeder-status">
        {litter.coBreederKennelId ? (
          <p>
            <strong>Co-Breeder Approval:</strong> {litter.coBreederApprovalStatus}
            {litter.coBreederApprovalStatus === 'Denied' && (
              <span> - You can delete this litter.</span>
            )}
          </p>
        ) : (
          <p>No co-breeder selected.</p>
        )}
      </div>
    )}
    <div className="litter-actions">
      <Link to={`/litter/${litter._id}`} className="action-button">
        View Litter
      </Link>
      {playerId === litter.playerId && litter.status === 'Pending' && (
        <button
          className="action-button remove-button"
          onClick={() => deleteLitter(litter._id)}
          disabled={litter.coBreederApprovalStatus === 'Pending'}
        >
          Delete Litter
        </button>
      )}
    </div>
  </div>
);

function MyLitters({ playerId }) {
  const [litters, setLitters] = useState([]);
  const [purchasedPuppies, setPurchasedPuppies] = useState([]);
  const [message, setMessage] = useState('');
  const [dogLookup, setDogLookup] = useState({});
  const [kennelLookup, setKennelLookup] = useState({});
  const [playerUsername, setPlayerUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!playerId) return;

    const fetchLitters = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-litters/${playerId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        setMessage(`Error fetching litters: ${error.message}`);
        return [];
      }
    };

    const fetchPurchasedPuppies = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/purchased-puppies/${playerId}`);
        if (!res.ok) {
          if (res.status === 404) return [];
          throw new Error(`Fetch failed: ${await res.text()}`);
        }
        const puppies = await res.json();
        // Ensure only transferred puppies not bred by player
        return puppies.filter(puppy => 
          puppy.status === 'Transferred' && 
          puppy.owner === playerId && 
          puppy.breederId !== playerId
        );
      } catch (error) {
        setMessage(`Error fetching purchased puppies: ${error.message}`);
        return [];
      }
    };

    const fetchDogDetails = async (dogId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/public-dog/${dogId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        console.error(`Error fetching dog ${dogId}:`, error.message);
        return null;
      }
    };

    const fetchKennelDetails = async (kennelId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/public-kennel/${kennelId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        console.error(`Error fetching kennel ${kennelId}:`, error.message);
        return null;
      }
    };

    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const [littersData, purchasedPuppiesData] = await Promise.all([
          fetchLitters(),
          fetchPurchasedPuppies(),
        ]);
        setLitters(littersData);
        setPurchasedPuppies(purchasedPuppiesData);

        const dogIds = new Set();
        const kennelIds = new Set();
        littersData.forEach(litter => {
          if (litter.sireId) dogIds.add(litter.sireId);
          if (litter.damId) dogIds.add(litter.damId);
          if (litter.bredBy) kennelIds.add(litter.bredBy);
          if (litter.coBreederKennelId) kennelIds.add(litter.coBreederKennelId);
        });
        purchasedPuppiesData.forEach(puppy => {
          if (puppy.sireId) dogIds.add(puppy.sireId);
          if (puppy.damId) dogIds.add(puppy.damId);
          if (puppy.bredBy) kennelIds.add(puppy.bredBy);
        });

        const dogData = await Promise.all(
          [...dogIds].map(dogId => fetchDogDetails(dogId).then(data => [dogId, data]))
        );
        const kennelData = await Promise.all(
          [...kennelIds].map(kennelId => fetchKennelDetails(kennelId).then(data => [kennelId, data]))
        );

        const playerIds = new Set();
        kennelData.forEach(([_, data]) => {
          if (data && data.playerId) playerIds.add(data.playerId);
        });

        const playerData = await Promise.all(
          [...playerIds].map(async (playerId) => {
            try {
              const res = await fetch(`${API_BASE_URL}/player-by-id/${playerId}`);
              if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
              const data = await res.json();
              return [playerId, data.username || 'Unknown'];
            } catch (error) {
              console.error(`Error fetching player username for ${playerId}:`, error.message);
              return [playerId, 'Unknown'];
            }
          })
        );

        const newDogLookup = Object.fromEntries(dogData.filter(([_, data]) => data));
        const newKennelLookup = {
          ...Object.fromEntries(
            kennelData.filter(([_, data]) => data).map(([id, data]) => [id, data.kennelName])
          ),
          ...Object.fromEntries(playerData),
        };

        setDogLookup(newDogLookup);
        setKennelLookup(newKennelLookup);
        setPlayerUsername(newKennelLookup[playerId] || 'Unknown');
      } catch (error) {
        console.error('Error in fetchAllData:', error.message);
        setMessage('Error fetching data: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [playerId]);

  const deleteLitter = async (litterId) => {
    if (!window.confirm('Are you sure you want to delete this litter? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/delete-litter/${litterId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitters(litters.filter(l => l._id !== litterId));
      setMessage(data.message || 'Litter deleted successfully');
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  if (!playerId) return <p>Please log in to manage litters.</p>;

  return (
    <div className="litters-container">
      <h1>My Litters</h1>
      {message && (
        <p className={message.includes('Error') ? 'error-message' : 'success-message'}>
          {message}
        </p>
      )}
      <div className="action-buttons">
        <button
          className="action-button"
          onClick={() => navigate('/breeding-permissions')}
        >
          Manage Breeding Permissions
        </button>
        <button
          className="action-button"
          onClick={() => navigate('/breed-litter')}
        >
          Breed a Litter
        </button>
      </div>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h2>Bred Litters</h2>
          {litters.length === 0 ? (
            <p>No litters found.</p>
          ) : (
            litters.map(litter => (
              <LitterDisplay
                key={litter._id}
                litter={litter}
                dogLookup={dogLookup}
                kennelLookup={kennelLookup}
                playerId={playerId}
                playerUsername={playerUsername}
                setLitters={setLitters}
                setMessage={setMessage}
                deleteLitter={deleteLitter}
              />
            ))
          )}
          <h2>Purchased Puppies</h2>
          {purchasedPuppies.length === 0 ? (
            <p>No purchased puppies to register.</p>
          ) : (
            <table className="purchased-puppies-table">
              <thead>
                <tr>
                  <th>Puppy</th>
                  <th>Breed</th>
                  <th>Color</th>
                  <th>Gender</th>
                  <th>Litter</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {purchasedPuppies.map((puppy, idx) => (
                  <tr key={idx}>
                    <td>{puppy.name || `Puppy ${puppy.puppyIndex + 1}`}</td>
                    <td>{puppy.breed || 'Unknown'}</td>
                    <td>{puppy.color || 'Unknown'}</td>
                    <td>{puppy.gender || 'Unknown'}</td>
                    <td>
                      <Link to={`/litter/${puppy.litterId}`}>View Litter</Link>
                    </td>
                    <td>
                      <button
                        className="action-button"
                        onClick={() => navigate('/register-dog', {
                          state: { litterId: puppy.litterId, puppy, puppyIndex: puppy.puppyIndex, sireId: puppy.sireId, damId: puppy.damId }
                        })}
                      >
                        Register
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default MyLitters;